import { useEffect } from 'react';

/**
 * HeroParallax
 * ------------
 * Subtle, bounded vertical glide on the homepage hero <img>. Reuses
 * the existing Lenis scroll instance exposed at `window.__lenis` and
 * mirrors the CSS-variable pattern from `MediaParallax.jsx`.
 *
 * Scoping — the hero <img> only:
 *   • target = document.querySelector('[data-testid="hero-section"] img')
 *   • never touches artwork frames (.img-wrap--natural), other .img-wrap
 *     wrappers (owned by ScrollLag), iframes, or anything under
 *     `.admin-scope`. There is no overlap with those systems because
 *     the hero <img> is not inside any `.img-wrap` container.
 *
 * Amplitude — measured live so no crop margin is ever exceeded:
 *   • The hero <img> is object-fit: cover in a viewport-height frame.
 *     Its scaled content overflows the box in whichever axis needs the
 *     smaller scale factor — that overflow is the "hidden crop margin".
 *   • contentH = boxW · (naturalH / naturalW)  (uniform-scale-by-width)
 *   • headroom  = max(0, (contentH − boxH) / 2)  (per-edge margin, px)
 *   • amplitude = min(headroom − 8, 16)
 *   • If headroom < 24 (portrait / mobile / near-square viewport where
 *     the image is cropped horizontally instead) → amplitude = 0 and
 *     --py is pinned to 0px. Recomputed on every window resize and
 *     once the image finishes loading (so naturalWidth is known).
 *
 * Transform-only: translate3d(0, var(--py), 0). No scale, no
 * object-position, no width/height/margin/padding — nothing that
 * reflows layout. Reduced-motion users get an early return in JS and
 * the media-query override in `index.css`.
 */

const MAX_AMPLITUDE = 120;  // test: use more of the existing crop headroom, px
const SAFETY_MARGIN = 8;    // px kept in reserve inside headroom
const MIN_HEADROOM  = 24;   // below this → no movement at all
const REDUCED_MOTION_Q = '(prefers-reduced-motion: reduce)';

export default function HeroParallax() {
    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        if (window.matchMedia && window.matchMedia(REDUCED_MOTION_Q).matches) return;

        const img = document.querySelector('[data-testid="hero-section"] img');
        const section = img && img.closest('[data-testid="hero-section"]');
        if (!img || !section) return;

        img.classList.add('hero-parallax');

        // Current effective translation amplitude — recomputed on load
        // + resize so mobile/portrait viewports collapse to zero cleanly.
        let amplitude = 0;

        const recompute = () => {
            const nW = img.naturalWidth || 0;
            const nH = img.naturalHeight || 0;
            const rect = img.getBoundingClientRect();
            const boxW = rect.width, boxH = rect.height;
            if (!nW || !nH || !boxW || !boxH) { amplitude = 0; return; }
            const boxAspect = boxW / boxH;
            const imgAspect = nW / nH;
            // When the box is wider (per unit height) than the image
            // aspect, `cover` scales the content by width, overflowing
            // vertically — that overflow is the vertical crop margin.
            // Otherwise the image already fills the box's height and no
            // vertical margin exists.
            let headroom = 0;
            if (boxAspect > imgAspect) {
                const contentH = boxW / imgAspect;
                headroom = Math.max(0, (contentH - boxH) / 2);
            }
            if (headroom < MIN_HEADROOM) {
                amplitude = 0;
                img.style.setProperty('--py', '0px');
            } else {
                amplitude = Math.min(headroom - SAFETY_MARGIN, MAX_AMPLITUDE);
            }
        };

        const update = () => {
            if (amplitude <= 0) return;
            const heroRect = section.getBoundingClientRect();
            const heroH = heroRect.height || 1;
            // Scroll progress across the hero section: 0 at rest, 1
            // when the hero has fully scrolled out of view.
            let progress = -heroRect.top / heroH;
            if (progress < 0) progress = 0;
            else if (progress > 1) progress = 1;
            const py = progress * amplitude;
            img.style.setProperty('--py', `${py.toFixed(2)}px`);
        };

        // Recompute fires on image-load and on resize -- both are one-off
        // position corrections, not continuous scroll tracking. Without a
        // transition, a load-triggered correction (e.g. the user has
        // already scrolled a little while the photo was still downloading)
        // snaps the image to its new offset instantly, which reads as a
        // glitch. Apply a short transition only for that one correction,
        // then remove it so real-time scroll-driven updates (below, via
        // Lenis) stay instant and 1:1 with scroll as before.
        const applyOnce = () => {
            recompute();
            img.style.transition = 'transform 0.6s cubic-bezier(0.16,1,0.3,1)';
            update();
            const clearTransition = () => { img.style.transition = ''; };
            img.addEventListener('transitionend', clearTransition, { once: true });
            setTimeout(clearTransition, 700); // safety net if transitionend never fires (e.g. no actual change)
        };

        if (img.complete && img.naturalWidth) applyOnce();
        else img.addEventListener('load', applyOnce, { once: true });

        const onResize = () => applyOnce();
        window.addEventListener('resize', onResize);

        // Subscribe to Lenis's own scroll event — no extra rAF loop,
        // no duplicated scroll listeners. Retry a few times while
        // SmoothScroll mounts on first navigation.
        let unsub = null;
        let attempts = 0;
        const attachLenis = () => {
            const lenis = window.__lenis;
            if (!lenis || typeof lenis.on !== 'function') {
                if (attempts++ < 40) setTimeout(attachLenis, 50);
                return;
            }
            lenis.on('scroll', update);
            unsub = () => { try { lenis.off?.('scroll', update); } catch { /* ignore */ } };
            update();
        };
        attachLenis();

        return () => {
            window.removeEventListener('resize', onResize);
            if (unsub) unsub();
            img.classList.remove('hero-parallax');
            img.style.removeProperty('--py');
        };
    }, []);

    return null;
}
